import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Search, Filter, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = {
  commande: "Commande",
  sav: "SAV",
  fournisseur: "Fournisseur",
  atelier: "Atelier",
  custom: "Autre",
};
const TYPE_COLORS = {
  commande: "bg-indigo-100 text-indigo-800 border-indigo-200",
  sav: "bg-amber-100 text-amber-800 border-amber-200",
  fournisseur: "bg-purple-100 text-purple-800 border-purple-200",
  atelier: "bg-slate-100 text-slate-800 border-slate-200",
  custom: "bg-gray-100 text-gray-800 border-gray-200",
};

/** Section "Détail des heures du mois" affichée sous le tableau mensuel.
 *  Trie / filtre par chantier (référence), type, jour, etc.
 */
const HoursMonthDetail = ({ employee, year, month }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("asc");
  const [refreshKey, setRefreshKey] = useState(0);
  const [editing, setEditing] = useState(null); // {id, start_time, end_time, comment}
  const [confirmDelete, setConfirmDelete] = useState(null); // entry object

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.getEmployeeWorkEntries(employee.id, { year, month });
        if (active) setData(data);
      } catch {
        toast.error("Erreur chargement du détail");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [employee.id, year, month, refreshKey]);

  const startEdit = (r) => {
    setEditing({ id: r.id, start_time: r.start_time || "", end_time: r.end_time || "", comment: r.comment || "" });
  };
  const cancelEdit = () => setEditing(null);
  const commitEdit = async () => {
    if (!editing) return;
    const { id, ...payload } = editing;
    try {
      await api.updateWorkEntry(id, payload);
      toast.success("Heures mises à jour");
      setEditing(null);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de mise à jour");
    }
  };
  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.deleteWorkEntry(confirmDelete.id);
      toast.success("Entrée supprimée");
      setConfirmDelete(null);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de suppression");
    }
  };

  const rows = useMemo(() => {
    const all = data?.entries || [];
    const q = search.trim().toLowerCase();
    let filtered = all;
    if (filterType !== "all") filtered = filtered.filter((e) => (e.entry_type || "custom") === filterType);
    if (q) filtered = filtered.filter((e) => (
      (e.reference_label || "").toLowerCase().includes(q)
      || (e.comment || "").toLowerCase().includes(q)
      || (e.date || "").includes(q)
    ));
    const cmp = (a, b) => {
      const get = (x) => {
        if (sortKey === "date") return `${x.date}T${x.start_time || "00:00"}`;
        if (sortKey === "ref") return (x.reference_label || "").toLowerCase();
        if (sortKey === "type") return x.entry_type || "";
        if (sortKey === "hours") return x.hours || 0;
        return 0;
      };
      const va = get(a), vb = get(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    };
    return [...filtered].sort(cmp);
  }, [data, search, filterType, sortKey, sortDir]);

  const total = rows.reduce((s, r) => s + (r.hours || 0), 0);
  const byRef = data?.by_reference || [];

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
  };

  return (
    <Card data-testid="hours-month-detail">
      <CardHeader className="pb-2 flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-sm">Détail du mois</CardTitle>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Chantier, commentaire, date…"
              className="h-8 pl-8 w-[260px]"
              data-testid="hours-detail-search"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="h-8 w-[160px]" data-testid="hours-detail-type-filter">
              <Filter className="h-3 w-3 mr-1" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous types</SelectItem>
              <SelectItem value="commande">Commande</SelectItem>
              <SelectItem value="sav">SAV</SelectItem>
              <SelectItem value="fournisseur">Fournisseur</SelectItem>
              <SelectItem value="atelier">Atelier</SelectItem>
              <SelectItem value="custom">Autre</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <>
            {/* Récap par référence (chantier) */}
            {byRef.length > 0 && (
              <div className="mb-4 flex flex-wrap gap-1.5" data-testid="hours-by-reference">
                {byRef.map((r) => (
                  <Badge key={r.label} variant="outline" className={`${TYPE_COLORS[r.entry_type] || TYPE_COLORS.custom} text-xs`}>
                    <span className="font-medium">{r.label}</span>
                    <span className="ml-1.5 font-mono">{r.hours.toFixed(2)}h</span>
                    <span className="ml-0.5 opacity-60">({r.entries})</span>
                  </Badge>
                ))}
              </div>
            )}

            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground italic text-center py-6">
                Aucune heure pointée sur cette période.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("date")} data-testid="sort-date">
                      Date {sortKey === "date" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                    </TableHead>
                    <TableHead>Créneau</TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("type")} data-testid="sort-type">
                      Type {sortKey === "type" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                    </TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("ref")} data-testid="sort-ref">
                      Chantier / Référence {sortKey === "ref" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                    </TableHead>
                    <TableHead>Commentaire</TableHead>
                    <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort("hours")} data-testid="sort-hours">
                      Heures {sortKey === "hours" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                    </TableHead>
                    <TableHead className="w-[100px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const isEditing = editing?.id === r.id;
                    return (
                    <TableRow key={r.id} data-testid={`hours-row-${r.id}`}>
                      <TableCell className="font-mono text-xs">
                        {(r.date || "").split("-").reverse().join("/")}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="time"
                              value={editing.start_time}
                              onChange={(e) => setEditing((s) => ({ ...s, start_time: e.target.value }))}
                              className="h-7 w-24 text-xs"
                              data-testid={`edit-start-${r.id}`}
                            />
                            <span>–</span>
                            <Input
                              type="time"
                              value={editing.end_time}
                              onChange={(e) => setEditing((s) => ({ ...s, end_time: e.target.value }))}
                              className="h-7 w-24 text-xs"
                              data-testid={`edit-end-${r.id}`}
                            />
                          </div>
                        ) : (
                          <span>{r.start_time} – {r.end_time}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`${TYPE_COLORS[r.entry_type] || TYPE_COLORS.custom} text-[10px]`}>
                          {TYPE_LABELS[r.entry_type] || "Autre"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{r.reference_label || r.custom_label || "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground italic max-w-[260px]">
                        {isEditing ? (
                          <Input
                            value={editing.comment}
                            onChange={(e) => setEditing((s) => ({ ...s, comment: e.target.value }))}
                            className="h-7 text-xs"
                            placeholder="Commentaire"
                            data-testid={`edit-comment-${r.id}`}
                          />
                        ) : (
                          <span className="truncate block" title={r.comment}>{r.comment || "—"}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono">{(r.hours || 0).toFixed(2)}h</TableCell>
                      <TableCell className="text-right">
                        {isEditing ? (
                          <div className="flex items-center justify-end gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={commitEdit} data-testid={`save-entry-${r.id}`}>
                              <Check className="h-3.5 w-3.5 text-emerald-600" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={cancelEdit}>
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(r)} title="Modifier" data-testid={`edit-entry-${r.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setConfirmDelete(r)} title="Supprimer" data-testid={`delete-entry-${r.id}`}>
                              <Trash2 className="h-3.5 w-3.5 text-destructive" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            <div className="mt-3 text-right text-sm">
              {rows.length} entrée(s) — Total : <span className="font-bold">{total.toFixed(2)}h</span>
            </div>
          </>
        )}
      </CardContent>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette entrée ?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete && (
                <>
                  Entrée du {(confirmDelete.date || "").split("-").reverse().join("/")}
                  &nbsp;de {confirmDelete.start_time} à {confirmDelete.end_time} sur {confirmDelete.reference_label || confirmDelete.custom_label || "—"}.
                  Cette action est irréversible.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={handleDelete} data-testid="confirm-delete-entry">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default HoursMonthDetail;
